import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  IconButton,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  PopoverTrigger,
  Text,
  useBreakpointValue,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useUserData } from "../../hooks/useUserData";
import { useAccountData } from "../../hooks/useAccountData";
import { SearchableMultiSelect } from "./SearchableMultiSelect";
import { CreateListForm, CreateListFormData } from "./CreateListForm";
import { ListPlusIcon } from "../icons/ListPlusIcon";
import { CaretLeftIcon } from "../icons/CaretLeftIcon";
import { CloseIcon } from "@chakra-ui/icons";
import {
  getComicLists,
  getReleaseLists,
  List,
  postAddComicToLists,
  postAddReleaseToLists,
  postRemoveComicFromList,
  postRemoveReleaseFromList,
} from "../../api/lists";
import { useListMutations } from "../../hooks/useListMutations";
import { ENTITY_TYPES, Z_INDEX_POPOVER } from "../../screens/root/constants";
import { READING_LISTS_SHELF_QUERY_KEY } from "../../screens/shelf/hooks/useReadingListsShelf";

interface AddToListButtonProps {
  entityType: "comic" | "release";
  entityId: number;
  size?: "sm" | "md" | "lg";
  variant?: "primary" | "secondary" | "tertiary";
  /**
   * Desktop popover anchor placement. Default `bottom-start` (grows
   * rightward from the trigger). Use `bottom-end` when the trigger sits
   * at the right edge of a narrow container (e.g. slide-in menu header)
   * so the popover grows leftward into the panel instead of off-screen.
   * Ignored on the mobile Drawer path, which is always bottom-sheet.
   */
  placement?: "bottom-start" | "bottom-end";
}

// Computes the current membership baseline: the set of list ids the entity
// is already a member of. The unified-checkbox UI initialises with this
// set selected; the user's toggles produce a working set; Apply diffs the
// working set against the baseline to derive adds and removes.
function computeBaseline(
  lists: List[] | undefined,
  entityType: "comic" | "release",
  entityId: number,
): Set<number> {
  const expectedTypeId =
    entityType === "comic" ? ENTITY_TYPES.COMICS : ENTITY_TYPES.RELEASES;
  const out = new Set<number>();
  if (!lists) return out;
  for (const list of lists) {
    const items = list.list_items;
    if (!items) continue;
    for (const item of items) {
      if (
        item.context_entity_id === entityId &&
        item.context_entity_type_id === expectedTypeId
      ) {
        out.add(list.id);
        break;
      }
    }
  }
  return out;
}

export function AddToListButton({
  entityType,
  entityId,
  size = "sm",
  variant = "tertiary",
  placement = "bottom-start",
}: AddToListButtonProps) {
  const [t] = useTranslation();
  const { userData } = useUserData();
  const accountData = useAccountData();
  // Visible to admins regardless of flag (legacy access path — admins use
  // this to manage curated lists in flag-off envs), and to anyone the
  // `reading_lists` feature flag is rolled out to.
  const canUseLists =
    !!userData?.is_admin || !!accountData?.features?.["reading_lists"];
  const { isOpen, onToggle, onClose } = useDisclosure();
  const triggerRef = useRef(null);
  const toast = useToast();
  const queryClient = useQueryClient();

  // Responsive shell selector. Popover on tablet+/desktop, full-width
  // bottom-sheet Drawer on phones — Popover anchored to a small icon at
  // a screen edge is cramped to the point of unusable on a 360px viewport.
  // ssr:false so the Suspense boundary doesn't see a hydration mismatch
  // when the breakpoint is undefined on first paint.
  const isMobile = useBreakpointValue(
    { base: true, md: false },
    { ssr: false },
  );

  const listType = entityType === "comic" ? "comics" : "releases";

  const [isCreateMode, setIsCreateMode] = useState(false);
  // Working set — list ids the entity should belong to after Apply. Init
  // and reset to the current membership baseline every time the popover
  // opens, so each session starts from a clean "nothing pending" state.
  const [pendingSelection, setPendingSelection] = useState<number[]>([]);

  // Fetch user's lists — only when the user can actually use this surface
  // (signed-in admin OR reading_lists flag on). Skips the network call
  // otherwise. Background tabs / long-idle sessions can let the user's
  // collection drift behind a wizard or detail-pane create from another
  // surface, so the staleness window is kept tight (30s); useListMutations
  // + the wizard cross-invalidate.
  const { data: listsResponse } = useQuery({
    enabled: canUseLists,
    queryKey: [`user-${entityType}-lists`],
    queryFn: entityType === "comic" ? getComicLists : getReleaseLists,
    staleTime: 30 * 1000,
  });

  const lists: List[] = listsResponse?.data.payload?.results || [];

  const baseline = useMemo(
    () => computeBaseline(lists, entityType, entityId),
    [lists, entityType, entityId],
  );

  // Seed the working set from baseline on open. Re-seed when baseline
  // changes (eg. another surface mutated the collection and our query
  // refetched). Doing this in an effect rather than on every render so
  // user-toggled state doesn't get clobbered mid-session.
  useEffect(() => {
    if (isOpen) {
      setPendingSelection([...baseline]);
      setIsCreateMode(false);
    }
  }, [isOpen, baseline]);

  // Wire create-list mutation through the shared hook (handles toasts +
  // inline-items API for create-and-add). The Apply / batched diff path
  // bypasses the hook's add/remove mutations because the diff here needs
  // baseline membership (which only this component knows) — the local
  // applyDiff orchestrator below owns that and fires the two API
  // primitives directly. invalidateListCaches still runs (via applyDiff
  // onSuccess below) so the shelf + this query stay in sync regardless
  // of which path produced the change.
  const { createListMutation, isLoading: mutationsLoading } = useListMutations({
    entityType,
    entityId,
    onCreateSuccess: () => setIsCreateMode(false),
    addCurrentEntityToNewList: true,
  });

  // Local applyDiff orchestrator — derives adds + removes from the
  // baseline/working-set diff and fires them in parallel. Why not put
  // this inside useListMutations? The diff calculation needs the
  // baseline, which only lives here (the hook is per-entity, not per-
  // surface), and the orchestration is specific to this UI (other
  // useListMutations consumers fire just one mutation at a time).
  const applyDiff = useMutation({
    mutationFn: async ({
      adds,
      removes,
    }: {
      adds: number[];
      removes: number[];
    }) => {
      const tasks: Promise<unknown>[] = [];
      if (adds.length > 0) {
        // Batched into one network call by the backend.
        tasks.push(
          entityType === "comic"
            ? postAddComicToLists(entityId, { list_ids: adds })
            : postAddReleaseToLists(entityId, { list_ids: adds }),
        );
      }
      // Remove API is per-list; fire concurrently.
      for (const listId of removes) {
        tasks.push(
          entityType === "comic"
            ? postRemoveComicFromList(entityId, listId)
            : postRemoveReleaseFromList(entityId, listId),
        );
      }
      await Promise.all(tasks);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`user-${entityType}-lists`] });
      queryClient.invalidateQueries({ queryKey: READING_LISTS_SHELF_QUERY_KEY });
      onClose();
    },
    onError: (error: unknown) => {
      const message =
        typeof error === "object" && error !== null && "message" in error
          ? String((error as { message: unknown }).message)
          : t("components.lists.addToList.genericErrorFallback");
      toast({
        title: t("components.lists.addToList.addError"),
        description: message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const adds = useMemo(
    () => pendingSelection.filter((id) => !baseline.has(id)),
    [pendingSelection, baseline],
  );
  const removes = useMemo(
    () =>
      [...baseline].filter((id) => !pendingSelection.includes(id)),
    [pendingSelection, baseline],
  );
  const pendingCount = adds.length + removes.length;
  const hasPending = pendingCount > 0;

  const isLoading =
    mutationsLoading || applyDiff.isPending;

  const handleApply = () => {
    if (!hasPending) {
      toast({
        title: t("components.lists.addToList.noListsSelectedTitle"),
        description: t(
          "components.lists.addToList.noListsSelectedDescription",
        ),
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    applyDiff.mutate({ adds, removes });
  };

  const handleCreate = (data: CreateListFormData) => {
    createListMutation.mutate(data);
  };

  const handleCreateCancel = () => {
    setIsCreateMode(false);
  };

  const handleCreateNewClick = () => {
    setIsCreateMode(true);
  };

  const handleCancel = () => {
    onClose();
  };

  // Hide entirely when the user can't use lists — signed out, or non-admin
  // without the `reading_lists` flag (the rest of the reading-list surface
  // is hidden too, so the trigger would dead-end). Surrounding surfaces
  // decide how to handle the gated experience.
  if (!canUseLists) return null;

  const headerLabel = isCreateMode
    ? t("components.lists.createListForm.title", {
        kind: t(
          listType === "comics"
            ? "components.lists.createListForm.kindComic"
            : "components.lists.createListForm.kindRelease",
        ),
      })
    : t("components.lists.addToList.header");

  // ---- Shared body / footer slots (used by both Popover and Drawer) ----

  const body = isCreateMode ? (
    <CreateListForm
      onCreateList={handleCreate}
      onCancel={handleCreateCancel}
      isCreating={createListMutation.isPending}
    />
  ) : (
    <Box px={3} py={3}>
      <SearchableMultiSelect
        lists={lists}
        selectedListIds={pendingSelection}
        onSelectionChange={setPendingSelection}
        listType={listType}
        entityId={entityId}
      />
    </Box>
  );

  const footer = !isCreateMode && (
    <Flex w="100%" justify="space-between" align="center" gap={2}>
      <Button variant="tertiary" size="sm" onClick={handleCreateNewClick}>
        {t("components.lists.addToList.newList")}
      </Button>
      <Flex gap={2}>
        <Button
          variant="secondary"
          size="sm"
          onClick={handleCancel}
          isDisabled={isLoading}
        >
          {t("components.lists.addToList.cancel")}
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={handleApply}
          isDisabled={!hasPending}
          isLoading={isLoading}
        >
          {hasPending
            ? t("components.lists.addToList.applyChanges", {
                count: pendingCount,
              })
            : t("components.lists.addToList.applyNoChanges")}
        </Button>
      </Flex>
    </Flex>
  );

  // ---- Mobile: bottom-sheet Drawer ----

  if (isMobile) {
    return (
      <>
        <Button
          ref={triggerRef}
          onClick={onToggle}
          variant={variant}
          size={size}
          leftIcon={<ListPlusIcon />}
        >
          {t("components.lists.addToList.triggerLabel")}
        </Button>
        <Drawer
          isOpen={isOpen}
          onClose={onClose}
          placement="bottom"
          autoFocus={false}
        >
          <DrawerOverlay />
          <DrawerContent
            borderTopRadius="16px"
            maxH="80vh"
            bg="charcoal.charcoal"
          >
            <DrawerHeader
              borderBottom="1px solid"
              borderColor="transparent.white.10"
              // Same editorial Bricolage treatment as the desktop popover
              // header so the two shells share visual identity.
              fontFamily="Bricolage Grotesque"
              fontSize="18px"
              fontWeight={500}
              color="dune.dune"
              sx={{
                fontVariationSettings: '"opsz" 14, "wdth" 100',
              }}
              display="flex"
              alignItems="center"
              gap={2}
            >
              {isCreateMode && (
                <IconButton
                  aria-label={t("components.lists.addToList.backToLists")}
                  icon={<CaretLeftIcon w="14px" h="14px" />}
                  size="xs"
                  variant="ghost"
                  onClick={() => setIsCreateMode(false)}
                  isDisabled={createListMutation.isPending}
                />
              )}
              <Text flex="1">{headerLabel}</Text>
            </DrawerHeader>
            <DrawerCloseButton />
            <DrawerBody p={0}>{body}</DrawerBody>
            {footer && (
              <DrawerFooter
                borderTop="1px solid"
                borderColor="transparent.white.10"
                px={3}
                py={3}
              >
                {footer}
              </DrawerFooter>
            )}
          </DrawerContent>
        </Drawer>
      </>
    );
  }

  // ---- Desktop: anchored Popover ----

  return (
    <Popover
      isOpen={isOpen}
      onClose={onClose}
      placement={placement}
      variant="filter"
      isLazy
    >
      <PopoverTrigger>
        <Button
          ref={triggerRef}
          onClick={onToggle}
          variant={variant}
          size={size}
          leftIcon={<ListPlusIcon />}
        >
          {t("components.lists.addToList.triggerLabel")}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        zIndex={Z_INDEX_POPOVER}
        // Fixed width so the create-mode swap (which renders the form
        // at its natural width) doesn't visually jolt the popover.
        // Bumped to 420px now that each row carries a 34×52 cover stack
        // on the left — list names need a comfortable runway after the
        // thumb + before the right-cluster of lock / "In list" /
        // checkbox.
        width="420px"
        // Cap at 80vh so the popover can never overflow the viewport
        // when anchored near the bottom of the screen. Body scrolls if
        // its content (long lists OR a taller create form) exceeds the
        // remaining space; header + footer stay pinned.
        maxH="80vh"
        display="flex"
        flexDirection="column"
      >
        <PopoverHeader
          borderBottomWidth="1px"
          borderColor="transparent.white.10"
          // Editorial header treatment — Bricolage Grotesque matches the
          // shelf detail pane's title typography so the popover feels
          // like part of the same family of lists surfaces rather than
          // a generic Chakra popover skin.
          fontFamily="Bricolage Grotesque"
          fontSize="16px"
          fontWeight={500}
          color="dune.dune"
          sx={{
            fontVariationSettings: '"opsz" 14, "wdth" 100',
          }}
          px={3}
          py={2.5}
          display="flex"
          alignItems="center"
          gap={2}
        >
          {isCreateMode && (
            <IconButton
              aria-label={t("components.lists.addToList.backToLists")}
              icon={<CaretLeftIcon w="14px" h="14px" />}
              size="xs"
              variant="ghost"
              onClick={() => setIsCreateMode(false)}
              isDisabled={createListMutation.isPending}
            />
          )}
          <Text flex="1">{headerLabel}</Text>
          <IconButton
            aria-label={t("components.lists.addToList.cancel")}
            icon={<CloseIcon w="10px" h="10px" />}
            size="xs"
            variant="ghost"
            onClick={onClose}
            isDisabled={isLoading}
          />
        </PopoverHeader>
        <PopoverBody p={0} flex="1" minH={0} overflowY="auto">
          {body}
        </PopoverBody>
        {footer && (
          <PopoverFooter px={3} py={2} flexShrink={0}>
            {footer}
          </PopoverFooter>
        )}
      </PopoverContent>
    </Popover>
  );
}
